import { useRef, useState, useEffect } from 'react';
import ArrowUpSLineIcon from 'remixicon-react/ArrowUpSLineIcon';
import { Button } from 'components/common';
import { renderImage, renderParagraph } from 'components/utils/render';
import { scrollWithOffset } from 'components/utils';
import { pushClickTileEvent, pushClickCtaEvent } from '../../../tracking/events';
import { captureInboundAttribution, appendTileUtms, getTileHref, getTileId, isOutboundUrl } from '../../../tracking/tiles';
import { registerMixpanelSuperProperties } from '../../../tracking/mixpanel';
import { WayCard as WayCardData } from '../AwarenessRaiserPage/AwarenessRaiserPage.models';
import { AwarenessRaiserReadMorePageProps } from './AwarenessRaiserReadMorePage.models';
import PledgeForm, { buildPledgeFormFromSignup, readAwarenessPledge, writeAwarenessPledge, HIDE_FORM_AFTER_PLEDGE } from '../AwarenessLanding/PledgeForm';
import {
    HeroSection,
    HeroMedia,
    HeroInner,
    HeroTitle,
    HeroCtaWrap,
    BodySection,
    BodyWrap,
    BodyInner,
    Intro,
    Body,
    SignupSection,
    SignupInner,
    SignupIntro,
    ReadMoreLink,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WayCard,
    WayCardImage,
    WayCardTitle,
    WayCardCta,
    BackToTop,
    BackToTopLink,
} from './AwarenessRaiserReadMorePage.style';

/**
 * Awareness Raiser Read More Page (DFE-98).
 *
 * Long form copy about what an awareness raiser is, with the sign up form at
 * the foot. Signing up swaps the form for the ways grid and persists.
 *
 * The pledge flag is shared with the landing page, so signing up on either is
 * recognised on both.
 */

const hasImage = (image?: object) => !!image && Object.keys(image).length > 0;

const trackWayCard = (card: WayCardData, index: number, pledgeContext: 'this_session' | 'returning' | 'ungated') => {
    if (!card.cta) return;
    const tileId = getTileId(card);
    const destinationUrl = appendTileUtms(getTileHref(card), tileId);
    pushClickTileEvent({
        linkText: card.cta.btnText,
        linkUrl: destinationUrl,
        tileId,
        position: index + 1,
        isOutbound: isOutboundUrl(destinationUrl),
        pledgeContext,
    });
};

const Chevron = () => (
    <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d="M9 5l7 7-7 7" />
    </svg>
);

const handleBackToTop = (event: React.MouseEvent) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

// The shared Button tracks its own clicks but without a location, so the CTA
// is tracked here instead to match the landing page.
const trackHeroCta = (button: { btnText: string, btnLink?: string }) => {
    pushClickCtaEvent({ linkText: button.btnText, linkUrl: button.btnLink, location: 'hero' });
};

const SIGNUP_ID = 'ar-readmore-signup';
const WAYS_ID = 'ar-readmore-ways';
const HEADER_OFFSET = 100;

// Falls back to the ways grid, which is what stands in for the form once the
// reader has pledged.
const scrollToSignup = (event: React.MouseEvent) => {
    const target = document.getElementById(SIGNUP_ID) || document.getElementById(WAYS_ID);
    if (!target) return;
    event.preventDefault();
    window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
        behavior: 'smooth',
    });
};

const AwarenessRaiserReadMorePage: React.FC<AwarenessRaiserReadMorePageProps> = ({
    cms,
    image,
    mobileImage,
    heroTitle,
    heroCta,
    intro,
    body,
    signup,
    ways,
}) => {
    const [hasSignedUp, setHasSignedUp] = useState(readAwarenessPledge);
    const [hadPledgedOnArrival] = useState(readAwarenessPledge);
    const [shouldScrollToWays, setShouldScrollToWays] = useState(false);
    const waysRef = useRef<HTMLElement>(null);

    const pledgeContext = !hasSignedUp ? 'ungated' : hadPledgedOnArrival ? 'returning' : 'this_session';

    const wayCards = (ways && ways.cards) || [];
    const showWays = hasSignedUp && wayCards.length > 0;

    useEffect(() => {
        if (shouldScrollToWays && waysRef.current) {
            scrollWithOffset(waysRef.current, -80);
            setShouldScrollToWays(false);
        }
    }, [shouldScrollToWays, hasSignedUp]);

    useEffect(() => {
        captureInboundAttribution();
    }, []);

    useEffect(() => {
        if (hadPledgedOnArrival) {
            registerMixpanelSuperProperties({ awareness_raiser: true });
        }
    }, [hadPledgedOnArrival]);

    const onSignupSuccess = () => {
        writeAwarenessPledge();
        setHasSignedUp(true);
        setShouldScrollToWays(true);
    };

    return (
        <>
            {(hasImage(image) || heroTitle) &&
                <HeroSection id="ar-readmore-top">
                    {hasImage(image) &&
                        <HeroMedia>{renderImage(image)}</HeroMedia>
                    }
                    {hasImage(mobileImage)
                        ? <HeroMedia isMobileOnly={true}>{renderImage(mobileImage)}</HeroMedia>
                        : hasImage(image) && <HeroMedia isMobileOnly={true}>{renderImage(image)}</HeroMedia>
                    }
                    <HeroInner>
                        {heroTitle && <HeroTitle>{heroTitle}</HeroTitle>}
                        {heroCta && heroCta.btnText &&
                            <HeroCtaWrap>
                                <Button {...heroCta} onClick={scrollToSignup} onClickCapture={() => trackHeroCta(heroCta)} />
                            </HeroCtaWrap>
                        }
                    </HeroInner>
                </HeroSection>
            }

            {((intro && intro.length > 0) || (body && body.length > 0)) &&
                <BodySection>
                    <BodyWrap>
                        <BodyInner>
                            {intro && intro.length > 0 &&
                                <Intro>{renderParagraph(intro)}</Intro>
                            }
                            {body && body.length > 0 &&
                                <Body>{renderParagraph(body)}</Body>
                            }
                        </BodyInner>
                    </BodyWrap>
                </BodySection>
            }

            {/* The form hides itself when the switch is on, so the band goes
                too rather than leaving the intro beside an empty column. */}
            {signup && !(HIDE_FORM_AFTER_PLEDGE && hasSignedUp) &&
                <SignupSection id={SIGNUP_ID}>
                    <SignupInner>
                        <SignupIntro>
                            {renderParagraph(signup.introText)}
                            {signup.readMoreCta && signup.readMoreCta.btnText &&
                                <ReadMoreLink><Button {...signup.readMoreCta} /></ReadMoreLink>
                            }
                        </SignupIntro>
                        <PledgeForm
                            cms={cms}
                            {...buildPledgeFormFromSignup(signup)}
                            hadPledged={hadPledgedOnArrival}
                            onSuccess={onSignupSuccess} />
                    </SignupInner>
                </SignupSection>
            }

            {showWays &&
                <WaysSection id={WAYS_ID} ref={waysRef}>
                    <WaysInner>
                        {ways && ways.eyebrow && <WaysEyebrow>{ways.eyebrow}</WaysEyebrow>}
                        {ways && ways.heading && <WaysHeading>{ways.heading}</WaysHeading>}
                        <WaysGrid>
                            {wayCards.map((card, index) =>
                                <WayCard
                                    key={index}
                                    href={card.cta ? appendTileUtms(getTileHref(card), getTileId(card)) : undefined}
                                    aria-label={card.cta ? `${card.title} — ${card.cta.btnText}` : card.title}
                                    onClick={() => trackWayCard(card, index, pledgeContext)}
                                    {...(card.cta && card.cta.btnType === 'external'
                                        ? { target: '_blank', rel: 'noopener' }
                                        : {})}
                                >
                                    {hasImage(card.image) &&
                                        <WayCardImage>{renderImage(card.image)}</WayCardImage>
                                    }
                                    {card.title && <WayCardTitle>{card.title}</WayCardTitle>}
                                    {card.cta && card.cta.btnText &&
                                        <WayCardCta>
                                            {card.cta.btnText}
                                            <Chevron />
                                        </WayCardCta>
                                    }
                                </WayCard>
                            )}
                        </WaysGrid>
                    </WaysInner>
                </WaysSection>
            }

            <BackToTop>
                <BackToTopLink href="#ar-readmore-top" onClick={handleBackToTop}>
                    <ArrowUpSLineIcon />
                    Back to top
                </BackToTopLink>
            </BackToTop>
        </>
    );
};

export default AwarenessRaiserReadMorePage;
